import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Shield, FileText, Scale, AlertCircle, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';

const Terms = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-black pt-20 pb-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate(-1)}
            className="text-white hover:text-gray-300 transition-colors mb-4 inline-flex items-center"
          >
            <ArrowLeft className="h-5 w-5 mr-2" />
            Back
          </button>
          <div className="flex items-center space-x-3 mb-4">
            <Scale className="h-10 w-10 text-red-500" />
            <h1 className="text-4xl font-bold text-white">Terms of Service</h1>
          </div>
          <p className="text-gray-400">Last updated: February 2025</p>
        </div>

        <div className="bg-white/5 backdrop-blur-sm rounded-xl p-8 border border-white/10 space-y-8 text-gray-300">
          {/* Introduction */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center">
              <FileText className="h-6 w-6 mr-2 text-red-500" />
              1. Introduction
            </h2>
            <p className="mb-4">
              Welcome to Seven Chapter Books ("we," "our," or "us"). By accessing or using our digital book platform 
              and services (the "Service"), you agree to be bound by these Terms of Service ("Terms"). 
              Please read them carefully before using our Service.
            </p>
            <p>
              These Terms apply to all users of the Service, including members who have been granted access 
              by our administrators.
            </p>
          </section>

          {/* Membership & Access */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center">
              <Shield className="h-6 w-6 mr-2 text-red-500" />
              2. Membership & Access
            </h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>Access to Seven Chapter Books is by invitation only. User accounts are created exclusively by administrators.</li>
              <li>You are responsible for maintaining the confidentiality of your account credentials.</li>
              <li>You must not share your login credentials with any third party.</li>
              <li>You agree to notify us immediately of any unauthorized use of your account.</li>
              <li>We reserve the right to suspend or terminate accounts that violate these Terms.</li>
            </ul>
          </section>

          {/* Content Usage */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">3. Content & Intellectual Property</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>All books, audiobooks, trailers, and related content are protected by copyright and intellectual property laws.</li>
              <li>Content is provided for your personal, non-commercial use only.</li>
              <li><strong className="text-white">Downloading, copying, or distributing any content is strictly prohibited.</strong></li>
              <li>You may not reproduce, modify, distribute, or create derivative works from any content.</li>
              <li>Screenshots, screen recordings, or any form of content capture is prohibited.</li>
            </ul>
          </section>

          {/* Prohibited Activities */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center">
              <AlertCircle className="h-6 w-6 mr-2 text-yellow-500" />
              4. Prohibited Activities
            </h2>
            <p className="mb-4">You agree NOT to:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Share, redistribute, or sell access to any content</li>
              <li>Use automated systems, bots, or scrapers to access the Service</li>
              <li>Attempt to bypass, disable, or interfere with security features</li>
              <li>Impersonate another user or provide false information</li>
              <li>Use the Service for any illegal or unauthorized purpose</li>
              <li>Reverse engineer, decompile, or disassemble any part of the Service</li>
              <li>Upload malicious code, viruses, or harmful content</li>
            </ul>
          </section>

          {/* Subscription & Payments */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">5. Subscription & Payments</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>Membership fees are handled offline by our administrators.</li>
              <li>Payment terms and pricing are communicated directly by administrators.</li>
              <li>Refunds are subject to our discretion and must be requested through the administrator.</li>
              <li>Access may be revoked for non-payment or violation of these Terms.</li>
            </ul>
          </section>

          {/* User Content */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">6. User Content & Reviews</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>You may submit reviews and ratings for books you have read.</li>
              <li>Reviews must be honest, respectful, and free of offensive content.</li>
              <li>We reserve the right to remove any review that violates these Terms.</li>
              <li>By submitting a review, you grant us a non-exclusive license to display it on our platform.</li>
            </ul>
          </section>

          {/* Disclaimers */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">7. Disclaimers</h2>
            <p className="mb-4">
              THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, 
              EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF 
              MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
            </p>
            <p>
              We do not warrant that the Service will be uninterrupted, secure, or error-free.
            </p>
          </section>

          {/* Limitation of Liability */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">8. Limitation of Liability</h2>
            <p>
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, SEVEN CHAPTER BOOKS SHALL NOT BE LIABLE FOR 
              ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS 
              OF PROFITS OR REVENUES, WHETHER INCURRED DIRECTLY OR INDIRECTLY, OR ANY LOSS OF DATA, 
              USE, GOODWILL, OR OTHER INTANGIBLE LOSSES.
            </p>
          </section>

          {/* Termination */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">9. Termination</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>We may terminate or suspend your access immediately, without prior notice, for any violation of these Terms.</li>
              <li>Upon termination, your right to use the Service will immediately cease.</li>
              <li>All provisions of these Terms which should reasonably survive termination shall survive.</li>
            </ul>
          </section>

          {/* Changes to Terms */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">10. Changes to Terms</h2>
            <p>
              We reserve the right to modify these Terms at any time. We will notify users of any material 
              changes by posting the new Terms on this page. Your continued use of the Service after any 
              changes constitutes acceptance of the new Terms.
            </p>
          </section>

          {/* Contact */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center">
              <CheckCircle className="h-6 w-6 mr-2 text-green-500" />
              11. Contact Us
            </h2>
            <p>
              If you have any questions about these Terms, please contact us through our 
              <a href="/contact" className="text-red-500 hover:text-red-400 ml-1">Contact Page</a>.
            </p>
          </section>
        </div>

        {/* Accept Button */}
        <div className="mt-8 text-center">
          <Button
            onClick={() => navigate('/')}
            className="bg-red-600 hover:bg-red-700 text-white px-8 py-3"
          >
            I Understand
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Terms;
